import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, input, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { FetchImageDirective } from '@core/directives/fetch-image.directive';
import {
  BreadcrumbComponent,
  BreadcrumbItem,
} from '@custom-components/general/breadcrumb/breadcrumb.component';
import { StatusBadgeType } from '@custom-components/general/status-chip/status-chip.component';
import { StatusChipComponent } from '@custom-components/general/status-chip/status-chip.component';
import { SkeletonBlockComponent } from '../../general/skeleton/skeleton-block/skeleton-block.component';

export interface PageHeaderBadge {
  label: string;
  status: StatusBadgeType;
  /** Optional Material icon name, forwarded to xemex-status-chip's icon input. */
  icon?: string;
}

@Component({
  selector: 'xemex-page-header',
  standalone: true,
  imports: [
    BreadcrumbComponent,
    StatusChipComponent,
    SkeletonBlockComponent,
    MatIconModule,
    FetchImageDirective,
    NgTemplateOutlet,
  ],
  styleUrl: './page-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.with-background]': 'withBackground()',
    '[class.with-tab-bar]': 'withTabBar()',
  },
  templateUrl: './page-header.component.html',
})
export class PageHeaderComponent {
  title = input.required<string>();
  subtitle = input<string>();
  /** `auto` (default) shrinks the title one size when a subtitle is present. `xl` keeps the
   * title at the largest heading size regardless — for pages that want a more prominent title. */
  titleSize = input<'auto' | 'xl'>('auto');
  /** Optional image (e.g. a device photo). Fetched authenticated via xemexFetchImage;
   * falls back to {@link icon} when absent or the fetch fails. */
  imageUrl = input<string>();
  /** Material icon name shown in the tinted avatar box when no image resolves.
   * When neither imageUrl nor icon is set, no avatar box is rendered. */
  icon = input<string>();
  /** Draws the icon in the outlined icon font. */
  iconOutlined = input<boolean>(false);
  breadcrumbs = input<BreadcrumbItem[]>([]);
  badges = input<PageHeaderBadge[]>([]);
  /** Forwarded to each xemex-status-chip's size input; `sm` (default) matches the original compact badges. */
  badgeSize = input<'sm' | 'lg'>('sm');
  /** `end` (default) puts the badges on the right of the header; `subtitle` puts them as small
   * uppercase pills right after the subtitle. */
  badgePlacement = input<'end' | 'subtitle'>('end');
  loading = input<boolean>(false);
  withBackground = input<boolean>(false);
  withTabBar = input<boolean>(false);

  protected readonly badgesBesideSubtitle = computed(() => this.badgePlacement() === 'subtitle');

  /** Whether a fetched image is currently shown; toggled by the xemexFetchImage directive. */
  protected readonly imageLoaded = signal(false);

  constructor() {
    // Reset the resolved flag whenever the source changes, so the icon shows until the new image loads.
    effect(() => {
      this.imageUrl();
      this.imageLoaded.set(false);
    });
  }
}
